import { useEffect, useRef, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useAtom as useStateSegment } from 'jotai';
import { loadPromptFieldOptions } from '../../services/Content';
import { FormFields } from '../../types/PromptForm';
import {
  CannedLanguage,
  CannedParametersResult,
  CannedParams,
} from '../../types/Content';
import {
  BrandDetailsFilterOptionTypes,
  brandDetailsFiltersStateSegment,
  BrandDetailsFiltersStateTypes,
} from '../../store/globalStore/stateSegments/brandDetailsFiltersStateSegment';
import { formatModulesResponse } from '../../utils/commonUtils';
import { APIRequestParams, CommonDataType } from '../../types/EmailModules';

export default function useBrandDetailsInputOptions({
  name,
  formField,
  params,
  shouldFetch,
}: {
  name: string;
  formField: FormFields;
  params: boolean | APIRequestParams;
  shouldFetch?: () => boolean;
}) {
  const key = `${name}Options`;
  const [filtersState, setFiltersState] = useStateSegment(
    brandDetailsFiltersStateSegment
  );
  const initialized = useRef(false);
  const location = useLocation();
  const fetchOptions = async (): Promise<void> => {
    const getOptions = (options: CannedParams[]) => {
      if (!options) return [];
      if (formField === FormFields.MODULE) {
        const commonOptions: CommonDataType[] = options.map(
          ({ id, name: optionName, label }) => {
            return {
              id,
              label,
              name: optionName || '',
            };
          }
        );
        return formatModulesResponse(commonOptions);
      }
      if (formField === FormFields.LANGUAGE) {
        return (options as unknown as CannedLanguage[]).map(
          ({ id, label, isPrimary, isFrequentlyUsed }) => ({
            id,
            label,
            isPrimary,
            name: id || '',
            isFrequentlyUsed,
          })
        );
      }
      if (formField === FormFields.BRAND) {
        return options.map(({ id, label, name: optionName, category }) => ({
          id,
          label,
          name: optionName || id || '',
          category,
        }));
      }
      return options;
    };

    setFiltersState((prev: BrandDetailsFiltersStateTypes) => ({
      ...prev,
      [key]: {
        ...filtersState[key as keyof BrandDetailsFilterOptionTypes],
        loading: true,
      },
    }));

    try {
      const response = await loadPromptFieldOptions(
        formField,
        params ? (params as APIRequestParams) : {}
      );
      const { data, success } = response as CannedParametersResult;
      if (success && data) {
        setFiltersState((prev: BrandDetailsFiltersStateTypes) => ({
          ...prev,
          [key]: {
            ...filtersState[key as keyof BrandDetailsFilterOptionTypes],
            options: getOptions(data.items || data),
            isFetched: true,
            isSuccess: true,
            loading: false,
          },
        }));
      } else {
        setFiltersState((prev: BrandDetailsFiltersStateTypes) => ({
          ...prev,
          [key]: {
            ...filtersState[key as keyof BrandDetailsFilterOptionTypes],
            options: [],
            isFetched: true,
            isSuccess: false,
            loading: false,
          },
        }));
      }
    } catch {
      setFiltersState((prev: BrandDetailsFiltersStateTypes) => ({
        ...prev,
        [key]: {
          ...filtersState[key as keyof BrandDetailsFilterOptionTypes],
          options: [],
          isFetched: true,
          isSuccess: false,
          loading: false,
        },
      }));
    }
  };
  const validateParams = (prms: APIRequestParams): boolean => {
    // Helper function to check if a value is valid (not empty or undefined)
    const isValidParam = (
      value: string | string[] | boolean | undefined
    ): boolean => {
      if (value === undefined || value === null) return false;
      if (typeof value === 'boolean') return false;
      if (typeof value === 'string') return value.trim().length > 0;
      if (Array.isArray(value))
        return value.length > 0 && value.some(item => item.trim().length > 0);
      return false;
    };

    // For indications, we need at least brandName to fetch data
    if (formField === FormFields.INDICATION) {
      return isValidParam(prms.brandName);
    }
    // For segments, we need brandName, audienceName, countryName, and indicationName
    if (formField === FormFields.SEGMENT) {
      return (
        isValidParam(prms.brandName) &&
        isValidParam(prms.audienceName) &&
        isValidParam(prms.countryName) &&
        isValidParam(prms.indicationName)
      );
    }
    // For audiences, we need at least brandName to fetch data
    if (formField === FormFields.AUDIENCE) {
      return isValidParam(prms.brandName);
    }
    // For brands, we need countryName and action
    if (formField === FormFields.BRAND) {
      return isValidParam(prms.countryName) && isValidParam(prms.action);
    }
    // For primary messages, we need brandName, countryName, indicationName, audienceName, and channelName
    if (
      formField === FormFields.PRIMARY_MESSAGES ||
      formField === FormFields.PRIMARYMESSAGE
    ) {
      return (
        isValidParam(prms.brandName) &&
        isValidParam(prms.countryName) &&
        isValidParam(prms.indicationName) &&
        isValidParam(prms.audienceName) &&
        isValidParam(prms.channelName)
      );
    }
    // For platforms, we need brandName, audienceName, and channelName
    if (formField === FormFields.PLATFORM) {
      return (
        isValidParam(prms.brandName) &&
        isValidParam(prms.audienceName) &&
        isValidParam(prms.channelName)
      );
    }
    // For languages, we need at least countryName
    if (formField === FormFields.LANGUAGE) {
      return isValidParam(prms.countryName);
    }
    // For modules, we need brandName and channel
    if (formField === FormFields.MODULE) {
      return isValidParam(prms.brandName) && isValidParam(prms.channel);
    }
    // Default behavior: require all params to be non-empty
    return Object.values(prms).every(isValidParam);
  };

  // Create a stable string representation of params for comparison
  const paramsString = useMemo(() => {
    if (typeof params === 'object' && params) {
      // Sort keys to ensure consistent comparison
      const sortedParams: Record<
        string,
        string | string[] | boolean | undefined
      > = {};
      Object.keys(params)
        .sort()
        .forEach(paramKey => {
          sortedParams[paramKey] = params[
            paramKey as keyof APIRequestParams
          ] as string | string[] | boolean | undefined;
        });
      return JSON.stringify(sortedParams);
    }
    return String(params);
  }, [params]);
  const previousParamsString = useRef<string>('');

  // Track shouldFetch result for comparison
  const currentShouldFetchResult = shouldFetch ? shouldFetch() : true;
  const previousShouldFetchResult = useRef<boolean>(true);

  // Check if this is static data that doesn't depend on params
  const isStaticData = typeof params === 'boolean' && params === false;

  useEffect(
    () => {
      // Check if this is initial load, params changed, or shouldFetch result changed
      const isInitialLoad = !initialized.current;
      const paramsChanged = previousParamsString.current !== paramsString;
      const shouldFetchChanged =
        previousShouldFetchResult.current !== currentShouldFetchResult;

      // For static data (params: false), only fetch on initial load
      if (isStaticData && !isInitialLoad) {
        return; // Don't re-fetch static data after initial load
      }

      // Only proceed if there's a reason to potentially fetch
      if (isInitialLoad || paramsChanged || shouldFetchChanged) {
        // Update tracking refs first to prevent repeated attempts
        previousParamsString.current = paramsString;
        previousShouldFetchResult.current = currentShouldFetchResult;

        // Determine if we can fetch based on params
        let canFetchBasedOnParams = false;

        if (isStaticData) {
          // Can fetch when params is false (static data that doesn't depend on form values)
          canFetchBasedOnParams = true;
        } else if (typeof params === 'object' && params) {
          // Can fetch when all required params are valid (non-empty strings)
          canFetchBasedOnParams = validateParams(params);
        }

        // Additional check if shouldFetch function is provided
        const canFetchBasedOnCondition = currentShouldFetchResult;

        // Only fetch if both conditions allow it
        if (canFetchBasedOnParams && canFetchBasedOnCondition) {
          initialized.current = true;
          fetchOptions();
        } else if (paramsChanged && typeof params === 'object') {
          // If params changed but we can't fetch yet, reset initialized to allow future fetches
          // This is important for cases where brand changes but other params aren't ready yet
          initialized.current = false;
        }
      }
    },
    isStaticData
      ? []
      : [paramsString, location.search, currentShouldFetchResult]
  );

  useEffect(() => {
    // Only reset for dynamic data (not static data like channels, tones, etc.)
    // Static data doesn't depend on URL parameters and shouldn't be refetched
    if (!isStaticData) {
      initialized.current = false;
      previousParamsString.current = '';
      previousShouldFetchResult.current = true;
    }
  }, [location.search, isStaticData]);

  return {
    [name]: {
      ...filtersState[key as keyof BrandDetailsFilterOptionTypes],
      fetchOptions,
    },
  };
}
